import { useCallback, useEffect, useState } from 'react'
import { ChevronUp, ChevronDown, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import { ApiError } from '@/lib/api/client'
import {
  getPaymentTermsTemplate,
  updatePaymentTermsTemplate,
  reorderPaymentTermsSteps,
} from '@/lib/api/v2/settings'
import type { PaymentTermsTemplateDto, PaymentTermsStepDto, PaymentTriggerType } from '@/types'

type EditableStep = {
  id: string
  invoiceType: 'deposit' | 'final'
  percentage: string // string for input control
  triggerType: PaymentTriggerType
  offsetDays: string // string for input control
  sortOrder: number
}

function toEditable(steps: PaymentTermsStepDto[]): EditableStep[] {
  return steps.map(s => ({
    id: s.id,
    invoiceType: s.invoiceType,
    percentage: String(s.percentage),
    triggerType: s.triggerType,
    offsetDays: String(s.offsetDays),
    sortOrder: s.sortOrder,
  }))
}

export function PaymentTermsSettings() {
  const [template, setTemplate] = useState<PaymentTermsTemplateDto | null>(null)
  const [steps, setSteps] = useState<EditableStep[]>([])
  const [gracePeriodDays, setGracePeriodDays] = useState<string>('3')
  const [tolerancePct, setTolerancePct] = useState<string>('5')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const t = await getPaymentTermsTemplate()
      setTemplate(t)
      setSteps(toEditable(t.steps))
      setGracePeriodDays(String(t.gracePeriodDays ?? 3))
      setTolerancePct(String(t.paymentTaskTolerancePct ?? 5))
    } catch {
      showErrorToast('Impossible de charger les conditions de paiement.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const percentageSum = steps.reduce((acc, s) => acc + (parseFloat(s.percentage) || 0), 0)
  const sumValid = Math.abs(percentageSum - 100) <= 0.01

  function updateStep(index: number, field: keyof EditableStep, value: string) {
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s))
  }

  function moveStep(index: number, dir: -1 | 1) {
    const newIndex = index + dir
    if (newIndex < 0 || newIndex >= steps.length) return
    const next = [...steps]
    ;[next[index], next[newIndex]] = [next[newIndex], next[index]]
    const reordered = next.map((s, i) => ({ ...s, sortOrder: (i + 1) * 10 }))
    setSteps(reordered)
    // Persist reorder immediately
    if (template) {
      reorderPaymentTermsSteps(reordered.map(s => ({ id: s.id, sortOrder: s.sortOrder }))).catch(() => {
        showErrorToast('Erreur lors de la réorganisation.')
      })
    }
  }

  function addStep() {
    const nextSort = steps.length > 0 ? Math.max(...steps.map(s => s.sortOrder)) + 10 : 10
    setSteps(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        invoiceType: 'deposit',
        percentage: '0',
        triggerType: 'Signature',
        offsetDays: '0',
        sortOrder: nextSort,
      },
    ])
  }

  function removeStep(index: number) {
    if (steps.length <= 1) return
    setSteps(prev => prev.filter((_, i) => i !== index))
  }

  async function handleSave() {
    if (!sumValid) return
    setSaving(true)
    try {
      const payload = steps.map(s => ({
        invoiceType: s.invoiceType,
        percentage: parseFloat(s.percentage) || 0,
        triggerType: s.triggerType,
        offsetDays: parseInt(s.offsetDays, 10) || 0,
        sortOrder: s.sortOrder,
      }))
      const graceDays = Math.max(0, parseInt(gracePeriodDays, 10) || 0)
      const tolerance = Math.min(20, Math.max(0, parseFloat(tolerancePct) || 0))
      const updated = await updatePaymentTermsTemplate(payload, graceDays, tolerance)
      setTemplate(updated)
      setSteps(toEditable(updated.steps))
      setGracePeriodDays(String(updated.gracePeriodDays ?? 3))
      setTolerancePct(String(updated.paymentTaskTolerancePct ?? 5))
      showSuccessToast('Conditions de paiement enregistrées.')
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        showErrorToast('La somme des pourcentages doit être égale à 100%.')
      } else {
        showErrorToast('Erreur lors de l\'enregistrement.')
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="text-sm text-muted-foreground">Chargement…</div>

  return (
    <div className="space-y-4">
      {/* Column headers */}
      <div className="grid grid-cols-[1fr_80px_160px_80px_80px] gap-2 px-1">
        <Label className="text-xs text-muted-foreground">Type</Label>
        <Label className="text-xs text-muted-foreground">%</Label>
        <Label className="text-xs text-muted-foreground">Déclencheur</Label>
        <Label className="text-xs text-muted-foreground">Décalage (j)</Label>
        <span />
      </div>

      {/* Steps list */}
      <div className="space-y-2">
        {steps.map((step, i) => (
          <div key={step.id} className="grid grid-cols-[1fr_80px_160px_80px_80px] gap-2 items-center">
            <select
              value={step.invoiceType}
              onChange={e => updateStep(i, 'invoiceType', e.target.value as 'deposit' | 'final')}
              className="h-8 text-sm border rounded-md px-2 bg-background"
            >
              <option value="deposit">Acompte</option>
              <option value="final">Solde</option>
            </select>
            <Input
              type="number"
              value={step.percentage}
              onChange={e => updateStep(i, 'percentage', e.target.value)}
              min={0}
              max={100}
              step={1}
              className="h-8 text-sm"
            />
            <select
              value={step.triggerType}
              onChange={e => updateStep(i, 'triggerType', e.target.value as PaymentTriggerType)}
              className="h-8 text-sm border rounded-md px-2 bg-background"
            >
              <option value="Signature">À la signature</option>
              <option value="PlacementDate">À la pose</option>
            </select>
            <Input
              type="number"
              value={step.offsetDays}
              onChange={e => updateStep(i, 'offsetDays', e.target.value)}
              className="h-8 text-sm"
            />
            <div className="flex items-center gap-0.5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => moveStep(i, -1)}
                disabled={i === 0}
              >
                <ChevronUp className="size-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => moveStep(i, 1)}
                disabled={i === steps.length - 1}
              >
                <ChevronDown className="size-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-destructive hover:text-destructive"
                onClick={() => removeStep(i)}
                disabled={steps.length <= 1}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add step */}
      <Button type="button" variant="outline" size="sm" onClick={addStep} className="gap-1.5">
        <Plus className="size-4" />
        Ajouter une étape
      </Button>

      {/* Sum indicator */}
      <div className={`text-sm font-medium ${sumValid ? 'text-green-600' : 'text-destructive'}`}>
        Somme : {percentageSum.toFixed(2)}%
        {!sumValid && <span className="ml-2 text-xs font-normal">doit être égal à 100%</span>}
      </div>

      {/* Grace period + Tolerance */}
      <div className="space-y-3 pt-2 border-t">
        <div className="flex items-center gap-3">
          <Label className="text-sm whitespace-nowrap">Délai de grâce</Label>
          <Input
            type="number"
            value={gracePeriodDays}
            onChange={e => setGracePeriodDays(e.target.value)}
            min={0}
            max={30}
            className="h-8 w-20 text-sm"
          />
          <span className="text-sm text-muted-foreground">jours ouvrables avant de passer "En retard"</span>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-sm whitespace-nowrap">Seuil de tolérance</Label>
          <Input
            type="number"
            value={tolerancePct}
            onChange={e => setTolerancePct(e.target.value)}
            min={0}
            max={20}
            step={0.5}
            className="h-8 w-20 text-sm"
          />
          <span className="text-sm text-muted-foreground">% — en dessous de ce seuil, les tâches de régularisation ne sont pas générées (sauf solde final)</span>
        </div>
      </div>

      {/* Save */}
      <div className="pt-2">
        <Button onClick={handleSave} disabled={!sumValid || saving} size="sm">
          {saving ? 'Enregistrement…' : 'Sauvegarder'}
        </Button>
      </div>
    </div>
  )
}
